import { when, runInAction } from "mobx";
import type { FetchByQueryResult, Segment } from "mobx-repository";
import { prop } from "ramda";
import type {
    PageInfo,
    UserGroupCreate,
    UserGroupsApi,
    UserGroupsListUserGroupsRequest,
    UserGroupSortKey,
    UserGroupUpdate,
    UserGroup,
} from "../../api";
import type { ApiResource } from "../../utils/elofleet-repository";
import { ElofleetRepository } from "../../utils/elofleet-repository";
import type { UUID } from "../../utils/uuid";
import { Validation } from "../../utils/validation";
import type { ServiceOwnUser } from "../services/service-own-user";
import { defaultPageSize } from "../../utils/constants";
export type UserGroupsQuery = Omit<UserGroupsListUserGroupsRequest, "page">;

export interface UserGroupsApiResource extends ApiResource {
    entity: UserGroup;
    query: UserGroupsQuery;
    update: UserGroupUpdate;
    create: UserGroupCreate;
    sortKey: UserGroupSortKey;
}

export class RepositoryUserGroups extends ElofleetRepository<UserGroupsApiResource> {
    constructor(
        private readonly userGroupsApi: UserGroupsApi,
        private readonly serviceOwnUser: ServiceOwnUser,
    ) {
        super();
        // Preload the first page once the user is logged in to avoid loading them individually later.
        when(
            () => this.serviceOwnUser.user !== undefined,
            () => this.byQuery({ pageSize: defaultPageSize }, { count: this.defaultCount }),
        );
    }

    public validation = {
        create: new Validation((userGroupCreate: UserGroupCreate) =>
            this.userGroupsApi.userGroupsValidateCreateUserGroup({
                userGroupCreate: userGroupCreate,
            }),
        ),
        update: new Validation((userGroupUpdate: UserGroupUpdate, userGroupId: UUID) =>
            this.userGroupsApi.userGroupsValidateUpdateUserGroup({ userGroupUpdate, userGroupId }),
        ),
    };

    protected async fetchByQuery(
        query: UserGroupsQuery,
        pagination: Segment,
    ): Promise<FetchByQueryResult<UserGroup>> {
        const page = pagination.offset / this.defaultCount;
        const entities = await this.userGroupsApi.userGroupsListUserGroups({
            ...query,
            page,
        });
        return { entities };
    }

    public fetchMetadata(_query: unknown): Promise<PageInfo> {
        return this.userGroupsApi.userGroupsListUserGroupsMetadata({ pageSize: defaultPageSize });
    }

    protected async fetchById(userGroupId: UUID): Promise<UserGroup | undefined> {
        return await this.userGroupsApi.userGroupsReadUserGroup({
            userGroupId,
        });
    }

    protected extractId = prop("id");

    public async update(userGroupId: UUID, userGroupUpdate: UserGroupUpdate): Promise<UserGroup> {
        const userGroup = await this.userGroupsApi.userGroupsUpdateUserGroup({
            userGroupId,
            userGroupUpdate,
        });
        await this.waitForIdle();
        runInAction(() => {
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(userGroup);
        });
        return userGroup;
    }

    public async create(userGroupCreate: UserGroupCreate): Promise<UserGroup> {
        const userGroup = await this.userGroupsApi.userGroupsCreateUserGroup({
            userGroupCreate,
        });
        await this.waitForIdle();
        runInAction(() => {
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(userGroup);
        });
        return userGroup;
    }

    public async delete(...userGroupIds: UUID[]): Promise<void> {
        await Promise.all(
            userGroupIds.map((userGroupId) =>
                this.userGroupsApi.userGroupsDeleteUserGroup({ userGroupId }),
            ),
        );
        await this.waitForIdle();
        this.reloadQuery({ pageSize: defaultPageSize });
    }
}
